// CountrySelectorPage — Pick country/region (affects tax, regulations, available features)
// Layout: search + scrollable country list with flags

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CountryFlag } from '../../components/Brand';

interface Country {
  code: string;
  name: string;
  region: 'Americas' | 'Europe' | 'Asia' | 'Oceania' | 'Africa';
  supported: boolean;
  taxNote?: string;
}

const COUNTRIES: Country[] = [
  // Americas
  { code: 'US', name: 'United States',     region: 'Americas', supported: true,  taxNote: '1099 reporting' },
  { code: 'CA', name: 'Canada',            region: 'Americas', supported: true },
  { code: 'MX', name: 'Mexico',            region: 'Americas', supported: true },
  { code: 'BR', name: 'Brazil',            region: 'Americas', supported: true },
  { code: 'AR', name: 'Argentina',         region: 'Americas', supported: false },
  // Europe
  { code: 'GB', name: 'United Kingdom',    region: 'Europe',   supported: true },
  { code: 'DE', name: 'Germany',           region: 'Europe',   supported: true },
  { code: 'FR', name: 'France',            region: 'Europe',   supported: true },
  { code: 'IT', name: 'Italy',             region: 'Europe',   supported: true },
  { code: 'ES', name: 'Spain',             region: 'Europe',   supported: true },
  { code: 'NL', name: 'Netherlands',       region: 'Europe',   supported: true },
  { code: 'CH', name: 'Switzerland',       region: 'Europe',   supported: true },
  { code: 'SE', name: 'Sweden',            region: 'Europe',   supported: true },
  // Asia
  { code: 'JP', name: 'Japan',             region: 'Asia',     supported: true },
  { code: 'CN', name: 'China',             region: 'Asia',     supported: false },
  { code: 'HK', name: 'Hong Kong',         region: 'Asia',     supported: true },
  { code: 'SG', name: 'Singapore',         region: 'Asia',     supported: true },
  { code: 'IN', name: 'India',             region: 'Asia',     supported: true },
  { code: 'KR', name: 'South Korea',       region: 'Asia',     supported: true },
  { code: 'AE', name: 'United Arab Emirates', region: 'Asia',  supported: true },
  // Oceania
  { code: 'AU', name: 'Australia',         region: 'Oceania',  supported: true },
  { code: 'NZ', name: 'New Zealand',       region: 'Oceania',  supported: true },
  // Africa
  { code: 'ZA', name: 'South Africa',      region: 'Africa',   supported: true },
  { code: 'NG', name: 'Nigeria',           region: 'Africa',   supported: false },
];

export function CountrySelectorPage() {
  const nav = useNavigate();
  const [selected, setSelected] = useState('US');
  const [q, setQ] = useState('');

  const filtered = COUNTRIES.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase().includes(q.toLowerCase()));
  const regions: Country['region'][] = ['Americas', 'Europe', 'Asia', 'Oceania', 'Africa'];

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl gap-2">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Country</div>
        <div className="w-9" />
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" /></svg>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search countries"
            data-testid="country-search"
            className="h-9 w-full rounded-[10px] bg-white border border-tf-border pl-9 pr-3 text-[14px] text-tf-text-1 focus:outline-none focus:ring-2 focus:ring-tf-blue/30 placeholder:text-tf-text-3"
          />
        </div>
      </div>

      {/* List */}
      {q ? (
        <div className="mx-4 mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-[14px] text-tf-text-3">No countries found</div>
          ) : filtered.map((c, i, a) => (
            <CountryRow key={c.code} country={c} selected={selected === c.code} onSelect={() => setSelected(c.code)} last={i === a.length - 1} />
          ))}
        </div>
      ) : (
        regions.map((r) => {
          const list = COUNTRIES.filter((c) => c.region === r);
          if (list.length === 0) return null;
          return (
            <div key={r} className="mt-3">
              <h2 className="px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">{r}</h2>
              <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
                {list.map((c, i, a) => (
                  <CountryRow key={c.code} country={c} selected={selected === c.code} onSelect={() => setSelected(c.code)} last={i === a.length - 1} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function CountryRow({ country: c, selected, onSelect, last }: { country: Country; selected: boolean; onSelect: () => void; last?: boolean }) {
  return (
    <button
      onClick={onSelect}
      disabled={!c.supported}
      data-testid={`country-${c.code}`}
      className={['flex w-full items-center gap-3 px-4 py-3 transition',
        last ? '' : 'border-b border-tf-border',
        c.supported ? 'active:bg-tf-surface-1' : 'opacity-50'
      ].join(' ')}
    >
      <CountryFlag code={c.code} size={32} />
      <div className="flex-1 min-w-0 text-left">
        <div className="text-[14px] font-semibold text-tf-text-1">{c.name}</div>
        {c.taxNote && <div className="text-[11px] text-tf-text-3">{c.taxNote}</div>}
      </div>
      {c.supported ? (
        selected ? (
          <svg viewBox="0 0 24 24" className="h-5 w-5 text-tf-blue" fill="currentColor"><path d="M9 16.17 5.53 12.7a1 1 0 0 0-1.41 1.41l4.18 4.18a1 1 0 0 0 1.41 0L20.29 8.71a1 1 0 0 0-1.41-1.41z" /></svg>
        ) : (
          <span className="text-[10px] font-semibold uppercase text-tf-profit">Available</span>
        )
      ) : (
        <span className="text-[10px] font-semibold uppercase text-tf-text-3">Soon</span>
      )}
    </button>
  );
}
